import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter, KpiEntityName } from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  CurrencyValue,
  Flex,
  formatNumber,
  LineChart,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useChartPeriod } from '@/hooks';

export default function HistoricTabs() {
  const [activeDisplayOption, setActiveDisplayOption] = useState('debt');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const displayOptions = useMemo(
    () => [
      { key: 'debt', value: 'Debt' },
      { key: 'collateral_usd', value: 'Collateral' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/groups/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const yScaleValueFormatter = useCallback(
    (value: number) => {
      switch (activeDisplayOption) {
        case 'debt':
          return `${formatNumber(value)}`;
        default:
          return `$${formatNumber(value)}`;
      }
    },
    [activeDisplayOption]
  );

  const valueFormatter = useCallback(
    (value: string | number) => {
      switch (activeDisplayOption) {
        case 'debt':
          return (
            <Flex flexDirection="row">
              <KpiEntityName
                symbol2={
                  <CryptoIcon
                    name="DAI"
                    address={tokensMapping('DAI').address}
                    size="3"
                  />
                }
                symbol1={
                  <CryptoIcon
                    name="USDS"
                    address={tokensMapping('USDS').address}
                    size="3"
                  />
                }></KpiEntityName>
              <CurrencyValue
                value={value}
                options={{
                  iconSize: '3'
                }}
              />
            </Flex>
          );

        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: 'USD'
              }}
            />
          );
      }
    },
    [activeDisplayOption]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <LineChart
          key={activeDisplayOption}
          data={data}
          datasets={[
            {
              xField: 'date',
              yField: activeDisplayOption
            }
          ]}
          yScaleValueFormatter={yScaleValueFormatter}
          valueFormatter={valueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }
  return <ChartSkeleton />;
}
